/**
 * The AccordionSection is the content portion for each individual Accordion within the AccordionGroup.
 *
 * @module views/components/AccordionSection
 * @memberof -Common
 */

import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Markdown from '@ulta/core/components/Markdown/Markdown';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { scrollToElement } from '@ulta/core/utils/scrollToElement/scrollToElement';

import Accordion from '@ulta/core/components/Accordion/Accordion';

/**
 * Transform AccordionSection props to Accordion's props
 * @param {object} props - AccordionSection props
 * @returns {object} AccordionSection variables and Accordion props
 */
export const transformAccordionSectionProps = ( props ) => {
  const customId = props.title ? props.title.replace( /\W+/g, '' ) : '';
  const id = props.id || customId;
  const windowHashMatchesId = typeof window !== 'undefined' && window.location.hash === `#${id}`;
  
  return {
    id,
    isSection: !props.detailAccordion,
    shouldScroll: windowHashMatchesId,
    accordionProps: spreadProps({
      title: props.title,
      isActive: windowHashMatchesId,
      expandAction: props.expandAction,
      collapseAction: props.collapseAction,
      titleTag: 'h3',
      ariaControls: id,
      ariaLabelledBy: id,
      variant: 'default',
      subTitle: props.subheading
    })
  };
};

/**
 * Represents a AccordionSection component
 *
 * @method
 * @param {AccordionSectionProps} props - React properties passed from composition
 * @returns AccordionSection
 */
export const AccordionSection = function( props ){
  const wrapperRef = useRef();
  const summaryRef = useRef();
  const [isActive, setIsActive] = useState(false);
  const { id, isSection, shouldScroll, accordionProps } = transformAccordionSectionProps( props );

  // Initialize isActive state based on URL hash
  useEffect(() => {
    if (shouldScroll) {
      setIsActive(true);
    }
  }, [shouldScroll]);

  // On load find the button element inside Accordion (from the new component structure)
  useEffect( () => {
    if( !wrapperRef.current ){
      return;
    }
    
    summaryRef.current = wrapperRef.current.querySelector( '[role="button"]' ) || 
                         wrapperRef.current.querySelector( 'summary' );
  }, [] );

  // If we have a hash, scroll to the summary element of associated accordion
  useEffect( () => {
    if( !summaryRef.current || !shouldScroll ){
      return;
    }

    scrollToElement( { el: summaryRef.current } );
  }, [summaryRef.current] );

  if( !accordionProps.title ){
    return null;
  }

  return (
    <div
      className={ classNames( 'AccordionSection', {
        'AccordionSection--section': isSection
      } ) }
      id={ id }
      ref={ wrapperRef }
    >
      <Accordion 
        { ...spreadProps({ 
          ...accordionProps,
          isActive: isActive,
          setIsActive: setIsActive
        }) }
      >
          <Markdown
            bodyStyle={ props.bodyStyle }
            cmsMarkdownContent={ props.body }
          />
      </Accordion>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef AccordionSectionProps
 * @type {object}
 * @property {string} title - sets the title value
 * @property {string} body - Sets the body value
 * @property {string} bodyStyle - Sets the body style tag
 * @property {string} subheading - Sets optional subheading for accordion
 * @property {object} expandAction - Action to perform on click to expand accordion
 * @property {object} collapseAction - Action to perform on click to collapse accordion
 * @property {boolean} detailAccordion - Flag that determines styling of "detail" vs "section" Accordion
 */
export const propTypes =  {
  /** Sets the text for display */
  title: PropTypes.string,
  /** Sets the body for display */
  body: PropTypes.string,
  /** Sets the body styling tag for display */
  bodyStyle: PropTypes.string,
  /** Sets optional subheading for accordion */
  subheading: PropTypes.string,
  /** Action to perform on click to expand accordion */
  expandAction: PropTypes.object,
  /** Action to perform on click to collapse accordion */
  collapseAction: PropTypes.object,
  /** Flag that determines styling of "detail" vs "section" Accordion */
  detailAccordion: PropTypes.bool
};

AccordionSection.propTypes = propTypes;

export default AccordionSection;